import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import ClampLines from '../ClampLines/ClampLines';
import Button from '../Button';
import Image from '../Image';
import Link from '../Link';
import Rga from '../Rga';

import { RATIO_16_9, WIDTH_400 } from '../../constants/imageServiceConstants';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import './DayOfEventCard.scss';

const testId = 'DayOfEventCard';

const DayOfEventCard = ({
  event,
  index,
  key,
  lastOfType,
  matchMedia,
  onBookEvent,
  onTodayEventClick,
  getTodayEventLink,
}) => {
  if (!event) {
    return null;
  }

  const {
    responsive_images: {
      urls: imageUrls,
      file_names: [fileName] = [],
    } = {},
    image_preview: imagePreview,
  } = event;

  return (
    <div
      key={key}
      className={cx(
        'DayOfEventCard--container',
        { 'DayOfEventCard--container--last': lastOfType }
      )}
    >
      <Image
        className="DayOfEventCard--image"
        src={imageUrls?.[fileName]?.[RATIO_16_9][WIDTH_400] ?? imagePreview}
      />
      <div className="DayOfEventCard--content">
        {event.is_global_dining_access
          ? <Rga variant="bold" />
          : (
            <TranslationProvider translationKey="event">
              <div className="DayOfEventCard--heading">
                Event
              </div>
            </TranslationProvider>
          )}
        <Link
          className="DayOfEventCard--name"
          href={getTodayEventLink ? getTodayEventLink(event) : null}
          onClick={(e) => onTodayEventClick(event, e)}
        >
          <ClampLines
            text={event.name}
            id={`today-event-name-${event.url_slug}`}
            buttons={false}
            lines={matchMedia?.md ? 1 : 5}
            ellipsis="..."
          />
        </Link>
        <div className="DayOfEventCard--selectedDate">
          {moment(event?.date).calendar().replaceAll(/[,.]/g, '')}
          {' '}
          at
          {' '}
          {moment(event?.time, 'HHmm').format('LT')}
        </div>
        <div className="DayOfEventCard--body">
          <ClampLines
            text={event.preview}
            id={`today-event-${event.url_slug}`}
            buttons={false}
            lines={2}
            ellipsis="..."
          />
        </div>
        <TranslationProvider translationKey="book_now">
          <Button
            aria-label={getTranslation({ translationKey: 'book_now', fallback: 'Book Now' })}
            className="DayOfEventCard--book-button"
            onClick={() => onBookEvent(event, index)}
            variant={event.is_global_dining_access ? 'rga' : 'primary'}
            data-test-id={`${testId}-day-of-event-book-now`}
          >
            Book Now
          </Button>

        </TranslationProvider>
      </div>
    </div>
  );
};

DayOfEventCard.propTypes = {
  event: PropTypes.shape({
    name: PropTypes.string.isRequired,
    preview: PropTypes.string.isRequired,
    is_global_dining_access: PropTypes.bool,
    url_slug: PropTypes.string.isRequired,
    image_preview: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    responsive_images: PropTypes.shape({
      file_names: PropTypes.arrayOf(PropTypes.string),
      urls: PropTypes.shape({
        [PropTypes.string]: PropTypes.shape({
          [PropTypes.string]: PropTypes.shape({
            [PropTypes.string]: PropTypes.string,
          }),
        }),
      }),
    }),
  }).isRequired,
  index: PropTypes.number,
  key: PropTypes.string,
  lastOfType: PropTypes.bool,
  onTodayEventClick: PropTypes.func,
  getTodayEventLink: PropTypes.func,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool.isRequired,
    sm: PropTypes.bool.isRequired,
    md: PropTypes.bool.isRequired,
    lg: PropTypes.bool.isRequired,
    xl: PropTypes.bool.isRequired,
  }).isRequired,
  onBookEvent: PropTypes.func.isRequired,
};

DayOfEventCard.defaultProps = {
  index: undefined,
  key: undefined,
  lastOfType: true,
  onTodayEventClick: () => {},
  getTodayEventLink: () => {},
};

DayOfEventCard.displayName = 'DayOfEventCard';
export default DayOfEventCard;
